import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { Action } from '@ngrx/store';
import { EnergyStarItemService } from '../../service/energy-star-item/energy-star-item.service';
import { EnergyStarSubmissionService } from '../../services/energy-star-submission.service';
import {
  loadEnergyStarItems,
  loadEnergyStarItemsHistory,
  loadEnergyStarItemsSuccess,
  loadEnergyStarItemsError,
  submitEnergyStarApplication,
  submitEnergyStarApplicationSuccess,
  submitEnergyStarApplicationError,
  updateEnergyStarStatus,
  updateEnergyStarStatusSuccess,
  updateEnergyStarStatusError,
} from '../actions/energy-star-item.actions';

@Injectable()
export class EnergyStarItemEffects {
  loadEnergyStarItems$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(loadEnergyStarItems),
      switchMap(({ facilityGroupId, year }) =>
        this.energyStarItemService.getEnergyStarItems(facilityGroupId, year).pipe(
          map(energyStarItems => loadEnergyStarItemsSuccess({ energyStarItems })),
          catchError(error => of(loadEnergyStarItemsError({ error }))),
        ),
      ),
    );
  });

  loadEnergyStarItemsHistory$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(loadEnergyStarItemsHistory),
      switchMap(({ facilityGroupId }) =>
        this.energyStarItemService.getEnergyStarItemsHistory(facilityGroupId).pipe(
          map(energyStarItems => loadEnergyStarItemsSuccess({ energyStarItems })),
          catchError(error => of(loadEnergyStarItemsError({ error }))),
        ),
      ),
    );
  });

  submitEnergyStarApplication$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(submitEnergyStarApplication),
      switchMap(({ facilityId, submission, facilityGroupId, year }) =>
        this.energyStarSubmissionService.submitEnergyStarApplication(facilityId, submission).pipe(
          map(energyStarItem => {
            // If we get an EnergyStarItem back, the submission was successful
            return submitEnergyStarApplicationSuccess({
              submissionId: energyStarItem.facilityId.toString(),
              facilityGroupId,
              year,
            });
          }),
          catchError(error => {
            let errorMessage = 'An error occurred during submission';

            // Extract error message from response
            const errorText = error?.error?.message || error?.message || '';

            // Look for errorDescription in XML response
            const errorDescriptionMatch = errorText.match(/errorDescription="([^"]+)"/);
            if (errorDescriptionMatch && errorDescriptionMatch[1]) {
              errorMessage = errorDescriptionMatch[1];
            } else if (errorText) {
              errorMessage = errorText;
            }

            return of(submitEnergyStarApplicationError({ error: errorMessage }));
          }),
        ),
      ),
    );
  });

  reloadAfterSubmission$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(submitEnergyStarApplicationSuccess),
      map(({ facilityGroupId, year }) => loadEnergyStarItems({ facilityGroupId, year })),
    );
  });

  updateEnergyStarStatus$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(updateEnergyStarStatus),
      switchMap(({ facilityId, facilityGroupId, year }) =>
        this.energyStarItemService.updateEnergyStarStatus(facilityId).pipe(
          map(() => updateEnergyStarStatusSuccess({ facilityId, facilityGroupId, year })),
          catchError(error => of(updateEnergyStarStatusError({ error: error?.message || 'Failed to update status' }))),
        ),
      ),
    );
  });

  reloadAfterStatusUpdate$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(updateEnergyStarStatusSuccess),
      map(({ facilityGroupId, year }) => loadEnergyStarItems({ facilityGroupId, year })),
    );
  });

  constructor(
    private actions$: Actions,
    private energyStarItemService: EnergyStarItemService,
    private energyStarSubmissionService: EnergyStarSubmissionService,
  ) {}
}
